/**
 * PrivacyScreen — Politique de confidentialité (RGPD-compliant)
 *
 * Conforme aux exigences :
 *   - RGPD (UE 2016/679)
 *   - Apple App Store Review Guideline §5.1.1 (i-iv)
 *   - California Consumer Privacy Act (CCPA) si user US
 *
 * Couvre :
 *   - Identité du responsable de traitement
 *   - Données collectées (catégories)
 *   - Finalités du traitement
 *   - Bases légales
 *   - Destinataires (sous-traitants : AWS, RevenueCat, etc.)
 *   - Durée de conservation
 *   - Droits des utilisateurs (accès, rectification, suppression, portabilité, opposition)
 *   - Procédure de suppression de compte (Apple §5.1.1(v))
 *   - Cookies / identifiants
 *   - Transferts hors UE
 *   - Contact DPO
 *
 * IMPORTANT : avant publication, faire valider par un avocat RGPD.
 */
import React, { useEffect } from 'react';
import {
  View, Text, ScrollView, StyleSheet, SafeAreaView,
  TouchableOpacity, StatusBar, Platform, useWindowDimensions,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import BackButton from '../components/BackButton';
import { openLegalUrl } from '../utils/openLegalUrl';

export default function PrivacyScreen({ navigation }) {
  const { dark, theme } = useTheme();
  const { lang } = useI18n();
  const en = lang === 'en';
  const s = makeStyles(theme);
  const { height: winHeight } = useWindowDimensions();

  useEffect(() => {
    openLegalUrl('privacy', lang);
    const t = setTimeout(() => { try { navigation?.goBack?.(); } catch {} }, 400);
    return () => clearTimeout(t);
  }, [lang, navigation]);

  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle={dark ? 'light-content' : 'dark-content'} />

      <View style={s.header}>
        <BackButton onPress={() => {
          // v1.12.191 : si on arrive en deep-link (pas d'historique), goBack()
          // ne fait rien → on navigue vers Home pour ne pas bloquer l'utilisateur.
          try {
            if (navigation.canGoBack && navigation.canGoBack()) navigation.goBack();
            else navigation.navigate('Main');
          } catch { try { navigation.navigate('Main'); } catch {} }
        }} />
        <Text style={s.title}>{en ? 'Privacy' : 'Confidentialité'}</Text>
        <View style={{ width: 38 }} />
      </View>

      <ScrollView
        contentContainerStyle={[s.content, { paddingBottom: 80 }]}
        style={Platform.OS === 'web' ? { height: winHeight - 60, overflowY: 'auto', WebkitOverflowScrolling: 'touch' } : undefined}
      >
        <Text style={s.lastUpdate}>{en ? 'Last updated: May 7, 2026' : 'Dernière mise à jour : 7 mai 2026'}</Text>

        <Text style={s.intro}>
          {en
            ? `Protecting your personal data is a priority for MyTenue. This policy transparently explains what information we collect, why, how we use it, and what your rights are. It complies with the General Data Protection Regulation (GDPR) and with Apple and Google requirements.`
            : `La protection de vos données personnelles est une priorité pour MyTenue. Cette politique explique de manière transparente quelles informations nous collectons, pourquoi, comment nous les utilisons et quels sont vos droits. Elle est conforme au Règlement Général sur la Protection des Données (RGPD) et aux exigences d'Apple et Google.`}
        </Text>

        {/* v1.12.153 — Mentions légales conformes LCEN art. 6-III */}
        <Section title={en ? '0. Legal information (service publisher)' : '0. Mentions légales (éditeur du service)'}>
          <Para>
            <Text style={s.bold}>{en ? 'Company name:' : 'Raison sociale :'}</Text> TO BE SMART
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Legal form:' : 'Forme juridique :'}</Text> {en ? 'SAS with share capital of EUR 1' : 'SAS au capital de 1 €'}
          </Para>
          <Para>
            <Text style={s.bold}>SIRET:</Text> 928 276 971 00027
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Registered office:' : 'Siège social :'}</Text> 17 rue Benoit Malon, 92150 Suresnes, France
          </Para>
          <Para>
            <Text style={s.bold}>Email :</Text>{' '}
            <Text style={s.contact}>contact@mytenue.com</Text>
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Phone:' : 'Téléphone :'}</Text> +33 7 87 45 47 32
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Publication director:' : 'Directeur de la publication :'}</Text> {en ? 'the legal representative of TO BE SMART' : 'le représentant légal de TO BE SMART'}
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Hosting provider:' : 'Hébergeur :'}</Text> {en
              ? `Amazon Web Services EMEA SARL — 38 avenue John F. Kennedy, L-1855 Luxembourg. Data is stored in the AWS Paris region (eu-west-3, France).`
              : `Amazon Web Services EMEA SARL — 38 avenue John F. Kennedy, L-1855 Luxembourg.
            Données stockées en région AWS Paris (eu-west-3, France).`}
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Target audience:' : 'Audience cible :'}</Text> {en
              ? `metropolitan France, French overseas territories, and French-speaking countries (Belgium, Switzerland, Luxembourg).`
              : `France métropolitaine, DOM-TOM, et pays francophones (Belgique, Suisse, Luxembourg).`}
          </Para>
        </Section>

        <Section title={en ? '1. Data controller' : '1. Responsable du traitement'}>
          <Para>
            {en ? 'The controller of your personal data is ' : 'Le responsable du traitement de vos données personnelles est la société '}
            <Text style={s.bold}>TO BE SMART</Text>
            {en
              ? ` (SAS), publisher of the MyTenue application, registered under SIRET 928 276 971 00027.`
              : `, éditrice de l'application MyTenue, immatriculée sous le SIRET 928 276 971 00027.`}
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Data Protection Officer (DPO):' : 'Délégué à la Protection des Données (DPO) :'}</Text>{' '}
            {en
              ? `TO BE SMART is not required to designate a DPO under article 37 GDPR (fewer than 250 employees, no large-scale processing of sensitive data, no systematic monitoring). The single GDPR contact point for exercising your rights is the address below.`
              : `TO BE SMART n'est pas tenue de désigner un DPO au sens de l'article 37 RGPD (effectif < 250, pas de traitement à grande échelle de données sensibles, pas de monitoring systématique). Le contact RGPD unique pour l'exercice de vos droits est l'adresse ci-dessous.`}
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Record of processing activities:' : 'Registre des activités de traitement :'}</Text>{' '}
            {en
              ? `in accordance with article 30 GDPR, we maintain an internal record of processing activities (purposes, data categories, retention periods, processors, security measures). This record is made available to the CNIL upon request.`
              : `conformément à l'article 30 RGPD, nous tenons à jour un registre interne des traitements (finalités, catégories de données, durées de conservation, sous-traitants, mesures de sécurité). Ce registre est tenu à disposition de la CNIL sur demande.`}
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Data Processing Agreements (DPAs):' : 'Accords de sous-traitance (DPA) :'}</Text>{' '}
            {en
              ? `each processor listed in section 4 has signed a data processing agreement compliant with article 28 GDPR (DPA), guaranteeing compliance with the regulation and the implementation of adequate technical and organisational measures.`
              : `chaque sous-traitant listé en section 4 a signé un accord de sous-traitance conforme à l'article 28 RGPD (DPA), garantissant le respect du règlement et l'application de mesures techniques et organisationnelles adéquates.`}
          </Para>
          <Para>
            {en
              ? `For any question regarding your personal data or the exercise of your rights, you can contact us at:`
              : `Pour toute question relative à vos données personnelles ou à l'exercice de vos droits, vous pouvez nous contacter à :`}
          </Para>
          <Text style={s.contact}>contact@mytenue.com</Text>
        </Section>

        <Section title={en ? '2. Data we collect' : '2. Données collectées'}>
          <Para>
            {en
              ? `We only collect data necessary to operate the service:`
              : `Nous collectons uniquement les données nécessaires au fonctionnement du service :`}
          </Para>
          <Sub title={en ? 'Identification data' : "Données d'identification"}>
            <Bullet>{en ? 'First and last name (from Apple/Google Sign-In or manual entry)' : 'Nom, prénom (depuis Apple/Google Sign-In ou saisie)'}</Bullet>
            <Bullet>{en ? 'Email address' : 'Adresse email'}</Bullet>
            <Bullet>{en ? 'Profile picture (optional)' : 'Photo de profil (optionnelle)'}</Bullet>
            <Bullet>{en ? 'Unique account identifier (Cognito UUID)' : 'Identifiant unique de compte (Cognito UUID)'}</Bullet>
          </Sub>
          <Sub title={en ? 'Personalisation data' : 'Données de personnalisation'}>
            <Bullet>{en ? 'Gender, date of birth' : 'Genre, date de naissance'}</Bullet>
            <Bullet>{en ? 'Body measurements (height, weight, body shape) — optional' : 'Mensurations (taille, poids, silhouette) — optionnelles'}</Bullet>
            <Bullet>{en ? 'Preferred clothing sizes' : 'Tailles de vêtements préférées'}</Bullet>
            <Bullet>{en ? 'Style preferences and budgets' : 'Préférences stylistiques et budgets'}</Bullet>
          </Sub>
          <Sub title={en ? 'Wardrobe data' : 'Données du dressing'}>
            <Bullet>{en ? 'Photographs of garments you upload' : 'Photographies des vêtements que vous téléchargez'}</Bullet>
            <Bullet>{en ? 'AI-generated metadata (colour, category, style)' : 'Métadonnées générées par IA (couleur, catégorie, style)'}</Bullet>
            <Bullet>{en ? 'Outfits you create and favourite looks' : 'Tenues créées et looks favoris'}</Bullet>
          </Sub>
          <Sub title={en ? 'Personal "My model" photo (optional)' : 'Photo personnelle « Mon mannequin » (optionnelle)'}>
            <Bullet>{en ? 'If you enable personalised virtual try-on, you may submit a full-body photo of yourself so we can generate try-on results that look like you (silhouette, height, face).' : "Si vous activez l'essayage virtuel personnalisé, vous pouvez nous transmettre une photo en pied pour générer des essayages qui vous ressemblent (silhouette, taille, visage)."}</Bullet>
            <Bullet>{en ? 'This photo may contain your face. It is used only to generate the try-on result and is never used for facial recognition or any biometric processing.' : "Cette photo peut contenir votre visage. Elle sert uniquement à générer le rendu d'essayage et ne fait l'objet d'aucune reconnaissance faciale ni traitement biométrique."}</Bullet>
            <Bullet>
              {en
                ? 'This photo is '
                : 'Cette photo est '}
              <Text style={s.bold}>{en ? 'encrypted at rest on our AWS S3 servers' : 'chiffrée au repos sur nos serveurs AWS S3'}</Text>
              {en
                ? ' (AES-256 encryption) and is used for your personal use only — it is never shared, sold, or used to train any model.'
                : " (chiffrement AES-256) et ne sert qu'à votre usage personnel — elle n'est jamais partagée, vendue ou utilisée pour entraîner un modèle."}
            </Bullet>
            <Bullet>
              {en ? 'You can ' : 'Vous pouvez la '}
              <Text style={s.bold}>{en ? 'delete it at any time' : 'supprimer à tout moment'}</Text>
              {en ? ' from the Profile screen → "My model".' : ' depuis l\'écran Profil → « Mon mannequin ».'}
            </Bullet>
            <Bullet>{en ? 'Deleting your account automatically and permanently removes this photo from our servers.' : 'La suppression de votre compte entraîne automatiquement la suppression définitive de cette photo de nos serveurs.'}</Bullet>
          </Sub>
          <Sub title={en ? 'Usage data' : "Données d'usage"}>
            <Bullet>{en ? 'Approximate location (city) for weather-based suggestions' : 'Localisation approximative (ville) pour les suggestions météo'}</Bullet>
            <Bullet>{en ? 'Recommendation and click history' : 'Historique des recommandations et clics'}</Bullet>
            <Bullet>{en ? 'Purchases made via partner links (cashback)' : 'Achats effectués via les liens partenaires (cashback)'}</Bullet>
            <Bullet>{en ? 'Anonymised usage statistics' : "Statistiques d'utilisation anonymisées"}</Bullet>
          </Sub>
          <Sub title={en ? 'Technical data' : 'Données techniques'}>
            <Bullet>{en ? 'Device type, OS and version' : "Type d'appareil, OS et version"}</Bullet>
            <Bullet>{en ? 'Push notification token (if enabled)' : 'Token de notification push (si activé)'}</Bullet>
            <Bullet>{en ? 'Anonymised error logs' : "Logs d'erreurs anonymisés"}</Bullet>
          </Sub>
        </Section>

        <Section title={en ? '3. Purposes and legal bases' : '3. Finalités et bases légales'}>
          <Para>{en ? 'We use your data to:' : 'Nous utilisons vos données pour :'}</Para>
          <Bullet><Text style={s.bold}>{en ? 'Provide the service' : 'Fournir le service'}</Text>{en ? ' — creating your virtual wardrobe, AI-generated looks (legal basis: performance of the contract)' : ' — création de votre dressing virtuel, génération de looks par IA (base légale : exécution du contrat)'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Personalise recommendations' : 'Personnaliser les recommandations'}</Text>{en ? ' — based on your preferences, measurements, and weather (legal basis: performance of the contract)' : ' — selon vos préférences, mensurations, météo (base légale : exécution du contrat)'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Manage your subscription' : 'Gérer votre abonnement'}</Text>{en ? ' — billing via Apple/Google (legal basis: performance of the contract)' : ' — facturation via Apple/Google (base légale : exécution du contrat)'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Send you notifications' : 'Vous envoyer des notifications'}</Text>{en ? ' — stock alerts, daily suggestions (legal basis: consent, withdrawable at any time)' : ' — alertes stock, suggestions quotidiennes (base légale : consentement, retirable à tout moment)'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Improve the application' : "Améliorer l'application"}</Text>{en ? ' — anonymised analytics (legal basis: legitimate interest)' : ' — analyses anonymisées (base légale : intérêt légitime)'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Pay out cashback' : 'Verser le cashback'}</Text>{en ? ' — tracking your partner purchases (legal basis: performance of the contract)' : ' — suivi de vos achats partenaires (base légale : exécution du contrat)'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Comply with our legal obligations' : 'Respecter nos obligations légales'}</Text>{en ? ' — taxation, fraud prevention (legal basis: legal obligation)' : ' — fiscalité, lutte contre la fraude (base légale : obligation légale)'}</Bullet>
        </Section>

        <Section title={en ? '4. Recipients of your data' : '4. Destinataires de vos données'}>
          <Para>
            {en
              ? `Your data is never sold to third parties. It may be disclosed to the following recipients, strictly within the scope of their assignments:`
              : `Vos données ne sont jamais vendues à des tiers. Elles peuvent être communiquées aux destinataires suivants, dans le cadre strict de leurs missions :`}
          </Para>
          <Sub title={en ? 'Technical processors (GDPR art. 28)' : 'Sous-traitants techniques (RGPD art. 28)'}>
            <Bullet><Text style={s.bold}>Amazon Web Services (AWS)</Text>{en ? ' — server hosting, database, and photo storage (Paris region, France)' : ' — hébergement des serveurs, base de données et stockage des photos (Région Paris, France)'}</Bullet>
            <Bullet><Text style={s.bold}>RevenueCat</Text>{en ? ' — technical management of iOS/Android subscriptions' : " — gestion technique des abonnements iOS/Android"}</Bullet>
            <Bullet><Text style={s.bold}>Anthropic (Claude AI)</Text>{en ? ' — AI image analysis to categorise your garments and generate recommendations' : " — analyse d'images par IA pour catégoriser vos vêtements et générer des recommandations"}</Bullet>
            <Bullet><Text style={s.bold}>Google Vertex AI</Text>{en ? ' — virtual try-on engine (combines your personal photo with a garment image). Data processed in the European region.' : " — moteur d'essayage virtuel (combine votre photo perso avec une image vêtement). Données traitées en région Europe."}</Bullet>
            <Bullet><Text style={s.bold}>Apple / Google</Text>{en ? ' — billing and authentication (Sign-In)' : ' — facturation et authentification (Sign-In)'}</Bullet>
            <Bullet><Text style={s.bold}>Google Analytics 4</Text>{en ? ' — anonymised audience measurement (web and mobile)' : " — mesure d'audience anonymisée (web et mobile)"}</Bullet>
            <Bullet><Text style={s.bold}>Resend</Text>{en ? ' — sending transactional emails (verification, forgotten password)' : " — envoi d'e-mails transactionnels (vérification, mot de passe oublié)"}</Bullet>
            <Bullet><Text style={s.bold}>Stripe</Text>{en ? ' — payments on the web version (PCI-DSS Level 1)' : ' — paiements sur la version web (PCI-DSS niveau 1)'}</Bullet>
            <Bullet><Text style={s.bold}>Open-Meteo</Text>{en ? ' — weather data (no personal data transmitted)' : ' — données météorologiques (sans transmission de données personnelles)'}</Bullet>
          </Sub>
          <Sub title={en ? 'Commercial partners' : 'Partenaires commerciaux'}>
            <Bullet>{en ? 'When you click on a product link, you are redirected to the partner\'s website (Zalando, ASOS, H&M, Zara, Cashmere, iQueens). Their own privacy policies apply.' : "Lorsque vous cliquez sur un lien produit, vous êtes redirigé(e) vers le site du partenaire (Zalando, ASOS, H&M, Zara, Cashmere, iQueens). Leurs propres politiques de confidentialité s'appliquent."}</Bullet>
            <Bullet>{en ? 'We send an anonymised identifier to track purchases and calculate cashback (CJ Tracking affiliate program).' : "Nous transmettons un identifiant anonymisé pour suivre les achats et calculer le cashback (programme d'affiliation CJ Tracking)."}</Bullet>
          </Sub>
        </Section>

        <Section title={en ? '5. Retention period' : '5. Durée de conservation'}>
          <Para>{en ? 'We retain your data:' : 'Nous conservons vos données :'}</Para>
          <Bullet><Text style={s.bold}>{en ? 'For as long as your account is active' : 'Tant que votre compte est actif'}</Text>{en ? ': profile, wardrobe, history' : ' : profil, dressing, historique'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? '3 years after your last login' : '3 ans après votre dernière connexion'}</Text>{en ? ': to allow you to come back if you wish' : ' : pour vous permettre de revenir si vous le souhaitez'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Deletion within 30 days' : 'Suppression dans un délai de 30 jours'}</Text>{en ? ' after explicit request or after 3 years of inactivity' : " après la demande explicite ou après 3 ans d'inactivité"}</Bullet>
          <Bullet><Text style={s.bold}>{en ? '10 years' : '10 ans'}</Text>{en ? ' for accounting and tax data (legal obligation)' : ' pour les données comptables et fiscales (obligation légale)'}</Bullet>
        </Section>

        <Section title={en ? '6. Your rights' : '6. Vos droits'}>
          <Para>
            {en
              ? `Under the GDPR, you have the following rights regarding your personal data:`
              : `Conformément au RGPD, vous disposez des droits suivants sur vos données personnelles :`}
          </Para>
          <Bullet><Text style={s.bold}>{en ? 'Right of access' : "Droit d'accès"}</Text>{en ? ' — obtain a copy of all your data' : ' — obtenir une copie de toutes vos données'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Right of rectification' : 'Droit de rectification'}</Text>{en ? ' — correct inaccurate information' : ' — corriger les informations inexactes'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Right to erasure' : "Droit à l'effacement"}</Text>{en ? ' — delete your account and your data ("right to be forgotten")' : " — supprimer votre compte et vos données (« droit à l'oubli »)"}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Right to portability' : 'Droit à la portabilité'}</Text>{en ? ' — receive your data in a structured format' : ' — recevoir vos données dans un format structuré'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Right to object' : "Droit d'opposition"}</Text>{en ? ' — refuse certain processing (notifications, analytics)' : ' — refuser certains traitements (notifications, analyses)'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Right to restriction' : 'Droit à la limitation'}</Text>{en ? ' — temporarily freeze processing' : ' — geler temporairement le traitement'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Right to withdraw consent' : 'Droit de retirer votre consentement'}</Text>{en ? ' — at any time for push notifications' : ' — à tout moment pour les notifications push'}</Bullet>
          <Para>
            {en ? 'To exercise these rights, please contact us at ' : 'Pour exercer ces droits, contactez-nous à '}
            <Text style={s.contactInline}>contact@mytenue.com</Text>
            {en ? '. We will respond within a maximum of one month.' : ". Nous répondrons dans un délai maximum d'un mois."}
          </Para>
          <Para>
            {en
              ? `You may also lodge a complaint with the CNIL (www.cnil.fr) if you believe your rights have not been respected.`
              : `Vous pouvez également déposer une réclamation auprès de la CNIL (www.cnil.fr) si vous estimez que vos droits n'ont pas été respectés.`}
          </Para>
        </Section>

        <Section title={en ? '7. Account deletion' : '7. Suppression de votre compte'}>
          <Para>
            {en
              ? `In compliance with Apple §5.1.1(v), you can delete your account at any time:`
              : `Conformément à l'exigence Apple §5.1.1(v), vous pouvez supprimer votre compte à tout moment :`}
          </Para>
          <Bullet>{en ? 'Open the MyTenue application' : "Ouvrez l'application MyTenue"}</Bullet>
          <Bullet>
            {en ? 'Go to ' : 'Allez dans '}
            <Text style={s.bold}>{en ? 'Profile → Settings' : 'Profil → Paramètres'}</Text>
          </Bullet>
          <Bullet>
            {en ? 'Tap ' : 'Touchez '}
            <Text style={s.bold}>{en ? '"Delete my account"' : '« Supprimer mon compte »'}</Text>
          </Bullet>
          <Bullet>{en ? 'Confirm your choice' : 'Confirmez votre choix'}</Bullet>
          <Para>
            {en
              ? `Deletion is permanent and results in the erasure of all your data within a maximum of 30 days, with the exception of data we are required to keep for legal reasons.`
              : `La suppression est définitive et entraîne l'effacement de toutes vos données dans un délai maximum de 30 jours, à l'exception des données que nous devons conserver pour des raisons légales.`}
          </Para>
        </Section>

        <Section title={en ? '8. Security' : '8. Sécurité'}>
          <Para>
            <Text style={s.bold}>{en ? 'All your personal data is protected and end-to-end encrypted' : "L'ensemble de vos données personnelles est protégé et chiffré"}</Text>
            {en ? '. We implement the following technical and organisational measures:' : ' de bout en bout. Nous mettons en œuvre les mesures techniques et organisationnelles suivantes :'}
          </Para>
          <Bullet><Text style={s.bold}>{en ? 'Encryption in transit' : 'Chiffrement en transit'}</Text>{en ? ' — TLS 1.3 for all communications between your device and our servers' : ' — TLS 1.3 pour toutes les communications entre votre appareil et nos serveurs'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Encryption at rest' : 'Chiffrement au repos'}</Text>{en ? ' — all data stored on our servers (wardrobe photos, "My model" personal photo, measurements, profile) is encrypted via AWS S3 SSE-S3 (AES-256), and the PostgreSQL database is encrypted via AWS KMS' : ' — toutes les données stockées sur nos serveurs (photos dressing, photo perso « Mon mannequin », mensurations, profil) sont chiffrées via AWS S3 SSE-S3 (AES-256) et la base PostgreSQL est chiffrée via AWS KMS'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'Private photos' : 'Photos privées'}</Text>{en ? ' — all photos are stored in private buckets, accessible only via temporary signed URLs (1h expiration)' : ' — toutes les photos sont stockées dans des buckets privés, accessibles uniquement via des URLs signées temporaires (expiration 1h)'}</Bullet>
          <Bullet>{en ? 'Multi-factor authentication on admin access' : "Authentification multi-facteurs sur les accès admin"}</Bullet>
          <Bullet>{en ? 'Continuous monitoring and access logging' : 'Surveillance continue et journalisation des accès'}</Bullet>
          <Bullet>{en ? 'Regular backups and disaster recovery plan' : "Sauvegardes régulières et plan de reprise d'activité"}</Bullet>
        </Section>

        <Section title={en ? '9. Transfers outside the European Union' : '9. Transferts hors Union Européenne'}>
          <Para>
            {en
              ? `Your data is primarily hosted in France (AWS Paris region). However, some processors may process your data outside the EU:`
              : `Vos données sont principalement hébergées en France (AWS région Paris). Certains sous-traitants peuvent toutefois traiter vos données en dehors de l'UE :`}
            {'\n\n'}
            • <Text style={s.bold}>Anthropic (Claude AI)</Text>{en ? ' — image analysis, processing in the United States under EU Standard Contractual Clauses (SCCs).' : " — analyse d'images, traitement aux États-Unis sous Clauses Contractuelles Types (CCT) UE."}{'\n'}
            • <Text style={s.bold}>Google Vertex AI</Text>{en ? ' — virtual try-on. Although we use the europe-west4 region (Netherlands), Google may technically replicate or process in the United States in certain cases, under EU SCCs and DPF (Data Privacy Framework) certification.' : ' — essayage virtuel. Bien que nous utilisions la région europe-west4 (Pays-Bas), Google peut techniquement répliquer ou traiter aux États-Unis dans certains cas, sous CCT UE et certification DPF (Data Privacy Framework).'}{'\n'}
            • <Text style={s.bold}>RevenueCat / Stripe / Resend / Google Analytics</Text>{en ? ' — may process in the United States under EU SCCs and DPF certification.' : ' — peuvent traiter aux États-Unis sous CCT UE et certification DPF.'}
            {'\n\n'}
            {en
              ? `All these transfers are governed by the Standard Contractual Clauses approved by the European Commission (Decision 2021/914) and/or by adherence to the EU-US Data Privacy Framework, ensuring a level of protection equivalent to the GDPR.`
              : `Tous ces transferts sont encadrés par les Clauses Contractuelles Types approuvées par la Commission Européenne (Décision 2021/914) et/ou par l'adhésion au Data Privacy Framework UE-US, garantissant un niveau de protection équivalent au RGPD.`}
          </Para>
        </Section>

        <Section title={en ? '10. Cookies and identifiers' : '10. Cookies et identifiants'}>
          <Para>
            {en
              ? `The Application uses the following cookies and identifiers:`
              : `L'Application utilise les cookies et identifiants suivants :`}
          </Para>
          <Bullet><Text style={s.bold}>{en ? 'Local storage on your device' : 'Stockage local de votre appareil'}</Text>{en ? ' (AsyncStorage) to remember your preferences and enable offline functionality' : ' (AsyncStorage) pour mémoriser vos préférences et permettre un fonctionnement hors-ligne'}</Bullet>
          <Bullet><Text style={s.bold}>{en ? 'No advertising identifier' : "Aucun identifiant publicitaire"}</Text>{en ? ' — MyTenue does NOT use the advertising identifier (IDFA / AAID) and shows no targeted advertising. Audience measurement relies only on an anonymous first-party identifier (see Google Analytics below).' : " — MyTenue n'utilise PAS l'identifiant publicitaire (IDFA / AAID) et n'affiche aucune publicité ciblée. La mesure d'audience repose uniquement sur un identifiant anonyme first-party (voir Google Analytics ci-dessous)."}</Bullet>
          <Bullet><Text style={s.bold}>Google Analytics 4 (GA4)</Text>{en ? ' — on the web version (mytenue.com), _ga / _ga_* cookies are placed to anonymously measure audience (page views, visit duration, traffic source). On mobile, the same statistics are collected via the GA4 Measurement Protocol. Data is anonymised (truncated IP) and hosted by Google under European Standard Contractual Clauses. You can disable tracking via your browser settings or the ' : " — sur la version web (mytenue.com), des cookies _ga / _ga_* sont déposés pour mesurer l'audience de manière anonymisée (pages vues, durée de visite, source de trafic). Sur mobile, les mêmes statistiques sont collectées via le Measurement Protocol GA4. Les données sont anonymisées (IP tronquée) et hébergées par Google selon les Clauses Contractuelles Types européennes. Vous pouvez désactiver le suivi via les paramètres de votre navigateur ou l'extension "}<Text style={s.bold}>Google Analytics Opt-out</Text>{en ? ' extension.' : '.'}</Bullet>
          <Bullet><Text style={s.bold}>Skimlinks / Sovrn / Impact</Text>{en ? ' — affiliate cookies placed on the web version to track partner purchases and calculate cashback. These cookies expire after 30 days.' : " — cookies d'affiliation déposés sur la version web pour suivre les achats partenaires et calculer le cashback. Ces cookies expirent après 30 jours."}</Bullet>
        </Section>

        <Section title={en ? '11. Minors' : '11. Mineurs'}>
          <Para>
            {en ? 'In accordance with the GDPR (article 8) and the French Data Protection Act (article 45), the Application is not intended for minors under ' : "Conformément au RGPD (article 8) et à la loi française Informatique et Libertés (article 45), l'Application n'est pas destinée aux mineurs de moins de "}
            <Text style={s.bold}>{en ? '15 years old in France' : '15 ans en France'}</Text>
            {en ? ' nor for minors under ' : ' ni aux mineurs de moins de '}
            <Text style={s.bold}>{en ? '16 years old in other European Union countries' : "16 ans dans les autres pays de l'Union européenne"}</Text>
            {en
              ? '. For minors aged 15 to 18, use of the Service requires the prior consent of the holder(s) of parental authority.'
              : ". Pour les mineurs âgés entre 15 et 18 ans, l'utilisation du Service requiert le consentement préalable du ou des titulaires de l'autorité parentale."}
          </Para>
          <Para>
            {en ? 'We do not knowingly collect personal data from users below the minimum age required. If you believe a minor below this age has provided us with data, please contact us at ' : "Nous ne collectons pas sciemment de données personnelles concernant des utilisateurs n'ayant pas atteint l'âge minimum requis. Si vous pensez qu'un mineur en deçà de cet âge nous a transmis des données, merci de nous contacter à l'adresse "}
            <Text style={s.contact}>contact@mytenue.com</Text>
            {en ? ': the account will be deleted immediately and all associated data erased.' : " : le compte sera supprimé sans délai et l'ensemble des données associées effacées."}
          </Para>
        </Section>

        <Section title={en ? '12. Changes' : '12. Modifications'}>
          <Para>
            {en
              ? `This policy may be updated to reflect legal, technical, or functional changes. Any substantial change will be notified to you within the application. The "last updated" date at the top of this document indicates the version in force.`
              : `Cette politique peut être mise à jour pour refléter des changements légaux, techniques ou fonctionnels. Toute modification substantielle vous sera notifiée dans l'application. La date de « dernière mise à jour » en haut de ce document indique la version en vigueur.`}
          </Para>
        </Section>

        <Section title={en ? '13. Contact' : '13. Contact'}>
          <Para>
            {en
              ? `For any question regarding this policy or your personal data:`
              : `Pour toute question relative à cette politique ou à vos données personnelles :`}
          </Para>
          <Text style={s.contact}>contact@mytenue.com</Text>
          <Para style={{ marginTop: 8 }}>
            {en ? 'Data Protection Officer (DPO):' : 'Délégué à la Protection des Données (DPO) :'}
          </Para>
          <Text style={s.contact}>contact@mytenue.com</Text>
        </Section>

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

// Sous-composants
function Section({ title, children }) {
  const { theme } = useTheme();
  const s = makeStyles(theme);
  return (
    <View style={s.section}>
      <Text style={s.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}
function Sub({ title, children }) {
  const { theme } = useTheme();
  const s = makeStyles(theme);
  return (
    <View style={s.subSection}>
      <Text style={s.subTitle}>{title}</Text>
      {children}
    </View>
  );
}
function Para({ children, style }) {
  const { theme } = useTheme();
  const s = makeStyles(theme);
  return <Text style={[s.para, style]}>{children}</Text>;
}
function Bullet({ children }) {
  const { theme } = useTheme();
  const s = makeStyles(theme);
  return (
    <View style={s.bulletRow}>
      <Text style={s.bulletDot}>•</Text>
      <Text style={s.bulletText}>{children}</Text>
    </View>
  );
}

const makeStyles = (theme) => StyleSheet.create({
  safe: { flex: 1, backgroundColor: theme.bg },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
    paddingTop: Platform.OS === 'android'
      ? (StatusBar.currentHeight || 24) + 8
      : 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: theme.bgBorder,
  },
  backBtn: {
    width: 38, height: 38,
    borderRadius: 19,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: theme.bgRaised || theme.bgSurface || '#F5F4F0',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: theme.bgBorder,
  },
  backIcon: {
    fontSize: 28,
    color: theme.text,
    fontWeight: '300',
    marginLeft: -2,
    marginTop: -2,
  },
  back: { fontSize: 14, color: theme.textSec, fontWeight: '500' }, // legacy
  title: { fontSize: 16, fontWeight: '600', color: theme.text, letterSpacing: 0.3 },
  content: { paddingHorizontal: 20, paddingTop: 20, paddingBottom: 60 },
  lastUpdate: {
    fontSize: 11,
    color: theme.textFaint,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 20,
  },
  intro: {
    fontSize: 14,
    lineHeight: 22,
    color: theme.textSec,
    marginBottom: 24,
  },
  section: { marginBottom: 24 },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.text,
    marginBottom: 10,
    letterSpacing: 0.2,
  },
  subSection: { marginTop: 6, marginBottom: 8 },
  subTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.text,
    marginTop: 8,
    marginBottom: 6,
    letterSpacing: 0.1,
  },
  para: {
    fontSize: 14,
    lineHeight: 22,
    color: theme.textSec,
    marginBottom: 10,
  },
  bold: { fontWeight: '600', color: theme.text },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: 6,
    paddingLeft: 4,
  },
  bulletDot: {
    width: 16,
    fontSize: 14,
    color: theme.accent,
    lineHeight: 22,
  },
  bulletText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 22,
    color: theme.textSec,
  },
  contact: {
    fontSize: 14,
    color: theme.accent,
    fontWeight: '500',
    marginTop: 4,
  },
  contactInline: {
    color: theme.accent,
    fontWeight: '500',
  },
});
